
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Check } from "lucide-react";

interface HeroSectionProps {
  email: string;
  setEmail: (email: string) => void;
  handleSubscribe: (e: React.FormEvent) => Promise<void>;
  isSubmitting: boolean;
  subscribed: boolean;
  handleLogin: () => void;
}

export const HeroSection = ({
  email,
  setEmail,
  handleSubscribe,
  isSubmitting,
  subscribed,
  handleLogin
}: HeroSectionProps) => {
  return (
    <header className="relative bg-gradient-to-b from-background to-muted py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="max-w-3xl space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="/lovable-uploads/d9d55f21-709c-4984-a6a6-05ded8e6ce2c.png" 
                alt="Agent V Logo" 
                className="h-12 w-auto" 
              />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                Agent V
              </h1>
              <span className="bg-primary/10 text-primary px-2 py-1 text-xs font-medium rounded">Beta</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-balance">
              <span className="text-primary">You do the show.</span> Agent V does the rest.
            </h2>
            
            <p className="text-xl md:text-2xl text-muted-foreground">
              Agent V is always in crush mode. Guest discovery. Research. Show notes in your style. Social media. Analytics. Crush.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              {!subscribed ? (
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
                  <div className="flex-grow">
                    <Label htmlFor="email" className="sr-only">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      disabled={isSubmitting}
                      className="h-12"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="h-12"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Subscribing..." : "Get Early Access"}
                  </Button>
                </form>
              ) : (
                <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 p-3 rounded-md">
                  <Check className="h-5 w-5 flex-shrink-0" />
                  <p>Thanks! We'll notify you when Agent V launches.</p>
                </div>
              )}
              
              <Button variant="outline" onClick={handleLogin} className="h-12">
                Log In
              </Button>
            </div>
          </div>
          
          <div className="rounded-lg bg-card p-8 border-2 border-border w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Upcoming Guests Pipeline</h3>
            <div className="space-y-3">
              {[
                { name: "John Smith", title: "Professor of Wireless Communication", entity: "Harvard University", date: "March 2nd, 2025" },
                { name: "Amanda Grimson", title: "CEO", entity: "EZ Grow", date: "Feb 14, 2025" },
                { name: "Michael Chen", title: "CTO", entity: "LoRa Alliance", date: "Feb 10, 2025" }
              ].map((guest, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-background rounded-md border border-border">
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    {guest.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{guest.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{guest.title}, {guest.entity}</p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{guest.date}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
